// ============================================================
// BLACK OWL - House Rules card (FB-24, FB-28)
// ============================================================
// Lobby setup: the host says what arrows mean in real life.
// Everyone else (players, spectators) sees the saved rules
// read-only. The server validates and locks them at game start.
// ============================================================

import { useEffect, useState } from "react";
import { apiCall } from "./api";
import { FONT_BODY, ELLIPSIS } from "./theme";
import {
  describePenalty, hasHouseRules, packageOptions, servePhrase,
  RULE_TEXT_MAX, RULE_WORTH_MIN, RULE_WORTH_MAX,
} from "./house-rules";

export const HOUSE_RULES_GUARDRAIL = "Penalties are voluntary. Keep them safe, legal, and fun.";
const ACK_TEXT = "I confirm these penalties are voluntary, safe, and legal, and everyone playing has agreed to them.";

// Ghost text only (D3): never saved, never applied
const ROWS = [
  { unit: "arrow", label: "1 arrow", action: "e.g. push-up", plural: "e.g. push-ups" },
  { unit: "cannonball", label: "Cannonball", worth: "e.g. 10", action: "e.g. run a lap", plural: "e.g. laps" },
  { unit: "meteor", label: "Meteor", worth: "e.g. 25", action: "e.g. 10 burpees", plural: "e.g. burpee sets" },
];

const toForm = (rules) => Object.fromEntries(ROWS.map(({ unit }) => [unit, {
  worth: rules?.[unit]?.worth ? String(rules[unit].worth) : "",
  action: rules?.[unit]?.action || "",
  plural: rules?.[unit]?.plural || "",
}]));

// Form -> request body; mirrors the server's D4 checks for instant feedback
function fromForm(form) {
  const rules = {};
  // Optional plural (D4): only kept alongside an action; blank means add "s"
  const pluralOf = (unit) => form[unit].plural.trim();
  if (form.arrow.action.trim()) rules.arrow = { action: form.arrow.action.trim(), plural: pluralOf("arrow") };
  for (const unit of ["cannonball", "meteor"]) {
    const action = form[unit].action.trim();
    const worthText = form[unit].worth.trim();
    if (!action && !worthText) continue;
    const label = unit === "cannonball" ? "Cannonball" : "Meteor";
    if (!action || !worthText) return { error: `${label} needs both a worth and an action, or leave both blank.` };
    const worth = Number(worthText);
    if (!Number.isInteger(worth) || worth < RULE_WORTH_MIN || worth > RULE_WORTH_MAX) {
      return { error: `${label} worth must be a whole number from ${RULE_WORTH_MIN} to ${RULE_WORTH_MAX}.` };
    }
    rules[unit] = { worth, action, plural: pluralOf(unit) };
  }
  if (rules.cannonball && rules.meteor && rules.meteor.worth <= rules.cannonball.worth) {
    return { error: "Meteor must be worth more arrows than a Cannonball." };
  }
  return { rules };
}

const inputStyle = {
  minWidth: 0, padding: "9px 10px", boxSizing: "border-box",
  fontSize: "13px", fontFamily: FONT_BODY, color: "#e8e6df",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px",
  outline: "none",
};

function RuleLine({ label, value }) {
  return (
    <div style={{ display: "flex", gap: "8px", fontSize: "13px", fontFamily: FONT_BODY, minWidth: 0 }}>
      <span style={{ color: "#7a7a8e", flexShrink: 0 }}>{label}</span>
      <span style={{ color: "#e8e6df", fontWeight: 600, ...ELLIPSIS }}>{value}</span>
    </div>
  );
}

// Saved rules as plain lines (read-only view, How to Play)
export function HouseRulesSummary({ rules }) {
  if (!hasHouseRules(rules)) {
    return <p style={{ fontSize: "13px", color: "#7a7a8e", margin: 0, fontFamily: FONT_BODY }}>No House Rules. Arrows are just arrows.</p>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {rules.arrow?.action && <RuleLine label="1 arrow =" value={servePhrase(rules.arrow, 1)} />}
      {packageOptions(rules).map((p) => (
        <RuleLine key={p.unit} label={`${p.unit === "cannonball" ? "Cannonball" : "Meteor"} (${p.worth} arrows) =`} value={servePhrase(p, 1)} />
      ))}
    </div>
  );
}

export default function HouseRulesCard({ lobbyId, username, rules, canEdit, locked }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => toForm(rules));
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", error: false });

  // Pick up saved rules (another device, or the server's cleaned text)
  const savedKey = JSON.stringify(rules || {});
  useEffect(() => { setForm(toForm(rules)); }, [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = canEdit && !locked;
  const draft = fromForm(form);
  const draftRules = draft.rules || {};
  const anyText = hasHouseRules(draftRules);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(rules));

  const setField = (unit, field, value) => {
    setForm((f) => ({ ...f, [unit]: { ...f[unit], [field]: value } }));
    setMessage({ text: "", error: false });
  };

  const save = async () => {
    if (draft.error) return setMessage({ text: draft.error, error: true });
    setSaving(true);
    try {
      const result = await apiCall("set_house_rules", {
        username, lobby_id: lobbyId, house_rules: draftRules, ack: anyText ? ack : false,
      });
      setForm(toForm(result.house_rules));
      setAck(false);
      setMessage({ text: hasHouseRules(result.house_rules) ? "House Rules saved." : "House Rules cleared.", error: false });
    } catch (err) {
      setMessage({ text: err.message, error: true });
    } finally {
      setSaving(false);
    }
  };

  // Live preview of what a penalty will say
  const arrowPreview = describePenalty(draftRules, "arrow", 3, 3);
  const firstPackage = packageOptions(draftRules)[0];
  const packagePreview = firstPackage && describePenalty(draftRules, firstPackage.unit, 1, firstPackage.worth);

  return (
    <div style={{
      marginBottom: "16px",
      background: "rgba(12, 12, 22, 0.85)",
      border: "1px solid rgba(201, 162, 39, 0.15)",
      borderRadius: "16px", overflow: "hidden",
    }}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: "10px",
          padding: "16px 20px", background: "transparent", border: "none",
          cursor: "pointer", textAlign: "left",
        }}
      >
        <span style={{ fontSize: "16px" }}>📜</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: "block", fontSize: "12px", fontWeight: 700, letterSpacing: "2px",
            textTransform: "uppercase", color: "#c9a227", fontFamily: FONT_BODY,
          }}>House Rules (optional)</span>
          <span style={{ display: "block", fontSize: "11px", color: "#7a7a8e", marginTop: "3px", fontFamily: FONT_BODY }}>
            {HOUSE_RULES_GUARDRAIL}
          </span>
        </span>
        {locked && (
          <span style={{
            fontSize: "10px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase",
            color: "#a0a0b0", padding: "3px 8px", border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "10px", fontFamily: FONT_BODY, flexShrink: 0,
          }}>🔒 Locked</span>
        )}
        <span style={{ color: "#7a7a8e", fontSize: "12px", flexShrink: 0 }}>{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div style={{ padding: "0 20px 18px" }}>
          {!editable ? (
            <>
              <HouseRulesSummary rules={rules} />
              {!canEdit && (
                <p style={{ fontSize: "11px", color: "#5a5a6e", margin: "10px 0 0", fontFamily: FONT_BODY }}>
                  Set by the host{locked ? "; locked for this game." : "."}
                </p>
              )}
            </>
          ) : (
            <>
              <p style={{ fontSize: "12px", color: "#a0a0b0", margin: "0 0 12px", fontFamily: FONT_BODY, lineHeight: 1.45 }}>
                Say what arrows mean in real life. Leave a row blank to skip it. Firing a Cannonball or Meteor costs exactly its worth in arrows.
              </p>
              {ROWS.map((row) => (
                <div key={row.unit} style={{ marginBottom: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#e8e6df", fontFamily: FONT_BODY, width: "76px", flexShrink: 0 }}>
                    {row.label}
                  </span>
                  {row.worth && (
                    <>
                      <input
                        aria-label={`${row.label} worth in arrows`}
                        inputMode="numeric"
                        value={form[row.unit].worth}
                        onChange={(e) => setField(row.unit, "worth", e.target.value.replace(/\D/g, "").slice(0, 3))}
                        placeholder={row.worth}
                        style={{ ...inputStyle, width: "64px", flexShrink: 0 }}
                      />
                      <span style={{ fontSize: "11px", color: "#7a7a8e", fontFamily: FONT_BODY, flexShrink: 0 }}>arrows</span>
                    </>
                  )}
                  <span style={{ fontSize: "12px", color: "#7a7a8e", flexShrink: 0 }}>=</span>
                  <input
                    aria-label={`${row.label} action`}
                    value={form[row.unit].action}
                    maxLength={RULE_TEXT_MAX}
                    onChange={(e) => setField(row.unit, "action", e.target.value)}
                    placeholder={row.action}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                </div>
                {/* Optional plural, once there's an action to pluralize */}
                {form[row.unit].action.trim() && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px", paddingLeft: "84px", minWidth: 0 }}>
                    <span style={{ fontSize: "11px", color: "#7a7a8e", fontFamily: FONT_BODY, flexShrink: 0 }}>plural</span>
                    <input
                      aria-label={`${row.label} plural`}
                      value={form[row.unit].plural}
                      maxLength={RULE_TEXT_MAX}
                      onChange={(e) => setField(row.unit, "plural", e.target.value)}
                      placeholder={`${row.plural} (optional)`}
                      style={{ ...inputStyle, flex: 1, padding: "6px 10px", fontSize: "12px" }}
                    />
                  </div>
                )}
                </div>
              ))}

              <div style={{
                margin: "12px 0", padding: "10px 12px", borderRadius: "8px",
                background: "rgba(201, 162, 39, 0.06)", fontSize: "12px", color: "#c0c0cc",
                fontFamily: FONT_BODY, lineHeight: 1.5,
              }}>
                <div style={ELLIPSIS}>
                  Preview: Dana owes {arrowPreview.what}{arrowPreview.serve && ` → time to serve ${arrowPreview.serve}`}
                </div>
                {packagePreview && (
                  <div style={ELLIPSIS}>
                    Preview: Dana owes {packagePreview.what} → time to serve {packagePreview.serve}
                  </div>
                )}
              </div>

              {anyText && (
                <label style={{ display: "flex", gap: "10px", alignItems: "flex-start", cursor: "pointer", marginBottom: "12px" }}>
                  <input
                    type="checkbox"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    style={{ width: "18px", height: "18px", marginTop: "1px", accentColor: "#c9a227", flexShrink: 0 }}
                  />
                  <span style={{ fontSize: "12px", color: "#c0c0cc", fontFamily: FONT_BODY, lineHeight: 1.45 }}>{ACK_TEXT}</span>
                </label>
              )}

              <button
                onClick={save}
                disabled={saving || !dirty || (anyText && !ack)}
                style={{
                  width: "100%", padding: "12px",
                  fontSize: "12px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase",
                  fontFamily: FONT_BODY, color: "#0a0a14",
                  background: "linear-gradient(135deg, #c9a227 0%, #e0be4a 50%, #c9a227 100%)",
                  border: "none", borderRadius: "8px",
                  cursor: saving || !dirty || (anyText && !ack) ? "default" : "pointer",
                  opacity: saving || !dirty || (anyText && !ack) ? 0.45 : 1,
                }}
              >{saving ? "Saving..." : anyText ? "Save House Rules" : "Clear House Rules"}</button>
            </>
          )}

          {message.text && (
            <p style={{
              fontSize: "12px", margin: "10px 0 0", fontFamily: FONT_BODY,
              color: message.error ? "#e85555" : "#32c850",
            }}>{message.text}</p>
          )}
        </div>
      )}
    </div>
  );
}
